import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  XMarkIcon,
  DocumentIcon,
  FolderIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { DocumentNode } from '../../types/documents';
import { searchService, SearchResult, SearchDocument, SearchMetadata } from '../../services/searchService';

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onDocumentSelect?: (document: DocumentNode) => void;
}

interface SearchResultItem {
  id: string;
  title: string;
  path: string;
  nodeType: string;
  createdAt: string;
  author: string;
  category?: string;
  tags?: string[];
  score?: number;
  highlights?: string[];
}

// Função para converter SearchDocument em SearchResultItem
const mapSearchDocumentToSearchResultItem = async (doc: any): Promise<SearchResultItem> => {
  // Função para obter nome do usuário
  const getAuthorName = async (metadataAuthor?: string) => {
    if (metadataAuthor && metadataAuthor.trim() !== '') {
      return metadataAuthor;
    }
    return 'Sistema';
  };

  // A nova API não tem createdBy, usar metadata.Author
  const authorName = await getAuthorName(doc.metadata?.Author);
  
  // Parsear metadata se for string
  let parsedMetadata: any = {};
  if (typeof doc.metadata === 'string') {
    try {
      parsedMetadata = JSON.parse(doc.metadata);
    } catch (error) {
      console.warn('Erro ao parsear metadata:', error);
      parsedMetadata = {};
    }
  } else {
    parsedMetadata = doc.metadata || {};
  }

  const result = {
    id: doc.id,
    title: doc.title,
    path: parsedMetadata?.Path || doc.path || `/documents/${doc.id}`,
    nodeType: doc.nodeType || 'document',
    createdAt: parsedMetadata?.UpdatedAt || doc.updatedAt || doc.createdAt || '',
    author: authorName,
    category: parsedMetadata?.Categories?.[0] || parsedMetadata?.category || '',
    tags: parsedMetadata?.Tags || [],
    score: doc.score || 1.0,
    highlights: []
  };
  
  return result;
};

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
  isOpen,
  onClose,
  onDocumentSelect
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const ITEMS_PER_PAGE = 25;

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    } else if (!isOpen) {
      // Limpar campo de pesquisa quando a janela é fechada
      setSearchQuery('');
      setSearchResults([]);
      setHasSearched(false);
      setSuggestions([]);
      setShowSuggestions(false);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalResults(0);
    }
  }, [isOpen]);

  // Debug: Monitorar mudanças de estado
  useEffect(() => {
    // Estados atualizados
  }, [isSearching, hasSearched, searchResults.length, totalResults, searchQuery]);

  // Debounce para sugestões
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        try {
          // Buscar sugestões baseadas em palavras-chave
          const suggestions = await searchService.getSuggestions(searchQuery, 5);
          setSuggestions(suggestions);
          setShowSuggestions(true);
          
          // Executar busca semântica em tempo real
          await handleSearch(searchQuery, 1);
        } catch (error) {
          console.error('Erro ao obter sugestões:', error);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
        setSearchResults([]);
        setHasSearched(false);
      }
    }, 500); // Aumentado para 500ms para dar tempo de digitar

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearch = async (query: string, page: number = 1) => {
    if (!query.trim()) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    try {
      // Usar o novo serviço de busca semântica
      const result: SearchResult = await searchService.semanticSearch({
        query: query.trim(),
        maxResults: ITEMS_PER_PAGE
      });
      
      // Verificar se os documentos têm a estrutura esperada
      if (result?.results && result.results.length > 0) {
        // Documentos encontrados
      }
      
      // A API retorna o formato correto com results
      let documents: any[] = [];
      let totalResults = 0;
      
      if (result?.results) {
        documents = result.results;
        totalResults = result.totalResults || result.results.length;
      } else {
        // Fallback para array vazio
        documents = [];
        totalResults = 0;
      }
      
      // Calcular metadados de paginação
      const totalPages = Math.ceil(totalResults / ITEMS_PER_PAGE);
      
      // Converter SearchDocument[] para SearchResultItem[]
      const searchResults = await Promise.all(documents.map(mapSearchDocumentToSearchResultItem));
      
      // Remover duplicatas baseadas no ID
      const uniqueResults = searchResults.filter((result, index, self) => 
        index === self.findIndex(r => r.id === result.id)
      );
      
      setSearchResults(uniqueResults);
      setTotalResults(uniqueResults.length);
      setTotalPages(totalPages);
      setCurrentPage(page);
      setHasSearched(true);
      setShowSuggestions(false);
      
    } catch (error) {
      console.error('Erro na busca:', error);
      setSearchResults([]);
      setTotalResults(0);
      setTotalPages(1);
      setCurrentPage(1);
      setHasSearched(true);
      setShowSuggestions(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSuggestions(false);
    // A busca será executada automaticamente pelo useEffect
  };

  const handlePageChange = (page: number) => {
    handleSearch(searchQuery, page);
  };

  const getFileIcon = (nodeType: string) => {
    switch (nodeType.toLowerCase()) {
      case 'folder':
        return <FolderIcon className="h-5 w-5 text-blue-500" />;
      case 'document':
      case 'word':
      case 'pdf':
      case 'excel':
      case 'powerpoint':
        return <DocumentIcon className="h-5 w-5 text-green-500" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-gray-500" />;
    }
  };



  const handleDocumentClick = (document: SearchResultItem) => {
    // Navegar para a página de detalhes do documento
    navigate(`/documents/${document.id}/details`);
    onClose();
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    // A busca será executada automaticamente pelo useEffect
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Buscar documentos</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Search Input */}
            <div className="p-4 border-b relative">
              <form onSubmit={handleSearchSubmit} className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Digite para buscar documentos..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
                
                {/* Sugestões */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 mt-1">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </form>
            </div>

            {/* Results */}
            <div className="flex-1 overflow-y-auto p-4 min-h-0">
              {isSearching ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Buscando...</span>
                </div>
              ) : hasSearched ? (
                <div>
                  {searchResults.length > 0 ? (
                    <>
                      <div className="mb-4">
                        <p className="text-sm text-gray-600">
                          Encontrados {totalResults} resultado{totalResults !== 1 ? 's' : ''} para "{searchQuery}"
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        {searchResults.map((result) => (
                          <div
                            key={result.id}
                            onClick={() => handleDocumentClick(result)}
                            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          >
                            <div className="flex items-start space-x-3">
                              {getFileIcon(result.nodeType)}
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-medium text-gray-900 truncate">
                                  {result.title}
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">
                                  {result.path}
                                </p>
                                {result.highlights && result.highlights.length > 0 && (
                                  <div className="mt-2">
                                    {result.highlights.slice(0, 2).map((highlight, index) => (
                                      <p key={index} className="text-xs text-gray-600" 
                                         dangerouslySetInnerHTML={{ __html: highlight }} />
                                    ))}
                                  </div>
                                )}

                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeftIcon className="h-4 w-4 mr-1" />
                            Anterior
                          </button>
                          
                          <span className="text-sm text-gray-700">
                            Página {currentPage} de {totalPages}
                          </span>
                          
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Próxima
                            <ChevronRightIcon className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Nenhum resultado encontrado para "{searchQuery}".</p>
                      <p className="text-sm text-gray-400 mt-2">Tente usar termos diferentes ou mais específicos.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MagnifyingGlassIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Digite algo para começar a buscar</p>
                  <p className="text-sm text-gray-400 mt-2">A busca será executada automaticamente conforme você digita</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}; 